export const utils = {
    obj: {
        /**
         * 深度克隆一个值 深拷贝。
         *
         * @param {any} value 需要被克隆的值。
         * @param {WeakMap} [map=new WeakMap()] 用于追踪已克隆的对象，防止循环引用。
         * @returns {any} 返回深度克隆后的值。
         *
         * @example
         * const original = { a: 1, b: { c: 2 } };
         * const cloned = obj.deepClone(original);
         * console.log(cloned); // { a: 1, b: { c: 2 } }
         *
         * 注意：该方法能处理基本类型、Date、RegExp、Map、Set、普通对象和数组的深度克隆。
         */
        deepClone: (value, map = new WeakMap()) => {
            // 如果是基本类型或null，直接返回
            if (typeof value !== 'object' || value === null) return value;

            // 处理Date、RegExp、Map、Set这些特殊对象
            if (value instanceof Date) return new Date(value);
            if (value instanceof RegExp) return new RegExp(value.source, value.flags);
            if (value instanceof Map) {
                const result = new Map();
                value.forEach((v, k) => result.set(k, utils.obj.deepClone(v, map)));
                return result;
            }
            if (value instanceof Set) {
                const result = new Set();
                value.forEach((v) => result.add(utils.obj.deepClone(v, map)));
                return result;
            }

            // 检查是否已经克隆过，以防止循环引用
            if (map.has(value)) return map.get(value);

            // 根据原始数据类型（数组或对象）创建空的结构
            const result = Array.isArray(value) ? [] : Object.create(Object.getPrototypeOf(value));
            map.set(value, result);

            // 递归复制对象的可枚举属性
            Object.keys(value).forEach(key => {
                result[key] = utils.obj.deepClone(value[key], map);
            });

            // 处理Symbol作为属性键的情况
            Object.getOwnPropertySymbols(value).forEach(symKey => {
                if (Object.getOwnPropertyDescriptor(value, symKey).enumerable) {
                    result[symKey] = utils.obj.deepClone(value[symKey], map);
                }
            });

            return result;
        },
    },

    date: {
        isValidDate: (str, format) => {
            // 替换日期格式中的年月日为相应的正则表达式
            const formatRegex = format
                .replace(/yyyy/g, '(\\d{4})')
                .replace(/MM/g, '(\\d{2})')
                .replace(/dd/g, '(\\d{2})');

            // 创建正则表达式
            const regex = new RegExp(`^${formatRegex}$`);
            const match = str.match(regex);

            if (!match) {
                return false;
            }

            // 提取年月日组件
            const yearIndex = format.indexOf('yyyy');
            const monthIndex = format.indexOf('MM');
            const dayIndex = format.indexOf('dd');

            const year = parseInt(match[yearIndex / 4 + 1], 10);
            const month = parseInt(match[monthIndex / 4 + 1], 10);
            const day = parseInt(match[dayIndex / 4 + 1], 10);

            // 检查月份和日期的有效性
            if (month < 1 || month > 12 || day < 1 || day > 31) {
                return false;
            }

            // 检查月份的天数
            if ((month === 4 || month === 6 || month === 9 || month === 11) && day > 30) {
                return false;
            }

            // 闰年检查
            if (month === 2) {
                const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
                if (day > 29 || (day === 29 && !isLeap)) {
                    return false;
                }
            }

            return true;
        },
        format: (dateStr) => {

            let date = new Date(dateStr);


            let year = date.getFullYear();
            let month = (date.getMonth() + 1).toString().padStart(2, '0');
            let day = date.getDate().toString().padStart(2, '0');
            let hours = date.getHours().toString().padStart(2, '0');
            let minutes = date.getMinutes().toString().padStart(2, '0');
            let seconds = date.getSeconds().toString().padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        /**
         * 获取当前时间
         * @param formatStr 指定的时间格式
         * @returns {*}
         */
        getNow: (formatStr) => {
            var now = new Date(); // 获取当前日期时间对象
            var year = now.getFullYear(); // 获取年份
            var month = now.getMonth() + 1; // 获取月份（注意月份从0开始，需要加1）
            var day = now.getDate(); // 获取日期
            var hour = now.getHours(); // 获取小时
            var minute = now.getMinutes(); // 获取分钟
            var second = now.getSeconds(); // 获取秒数

            // 按照传入的格式进行格式化
            return formatStr
                .replace('yyyy', year)
                .replace('MM', utils.string.padZero(month))
                .replace('dd', utils.string.padZero(day))
                .replace('HH', utils.string.padZero(hour))
                .replace('mm', utils.string.padZero(minute))
                .replace('ss', utils.string.padZero(second));
        },
    },

    array: {
        /**
         * 判断集合是否包含某个元素
         * @param list
         * @param item
         * @returns {boolean}
         */
        contains: (list, item) => {
            if (!Array.isArray(list)) {
                throw new Error('list must be an array');
            }
            return list.indexOf(item) !== -1;
        },

        /**
         * 将数组用分隔符连接成字符串
         * @param arr
         * @param separator
         * @returns {*}
         */
        joinArrToStr: (arr, separator) => {
            // 检查arr是否为数组，separator是否为字符串
            if (!Array.isArray(arr) || typeof separator !== 'string') {
                throw new Error("输入无效: 第一个参数必须是数组，第二个参数必须是字符串");
            }

            return arr.filter(item => utils.string.vNotEmpty(item)).join(separator);
        },

        /**
         * 合并两个数组，可选是否去除重复元素
         * @param array1 第一个数组
         * @param array2 第二个数组
         * @param deduplicate 是否去重，默认为 true
         * @returns {Array} 合并后的新数组，根据 deduplicate 参数决定是否去重
         */
        mergeArrays: (array1, array2, deduplicate = true) => {
            if (!Array.isArray(array1) || !Array.isArray(array2)) {
                throw new Error('两个参数都应该是数组。');
            }
            const mergedArray = [...array1, ...array2];
            return deduplicate ? Array.from(new Set(mergedArray)) : mergedArray;
        },

        /**
         * 从数组中随机获取指定数量的元素，确保获取的元素不重复
         * @param {Array} arr - 输入的数组
         * @param {number} count - 需要获取的元素数量
         * @returns {Array} - 随机获取的元素数组
         */
        getRandomElements: (arr, count) => {
            if (count > arr.length) {
                throw new Error("指定的数量超过数组长度");
            }

            // 复制原数组，以免修改原数组
            const copyArr = arr.slice();

            // 用于存储获取的随机元素
            const result = [];

            // 从复制的数组中随机获取元素
            while (result.length < count) {
                const randomIndex = Math.floor(Math.random() * copyArr.length);
                const element = copyArr[randomIndex];

                // 确保元素不重复
                if (!result.includes(element)) {
                    result.push(element);
                    copyArr.splice(randomIndex, 1); // 从复制的数组中删除已获取的元素
                }
            }

            return result;
        },
        /**
         * 比较两个数组是否相等
         * @param arr1
         * @param arr2
         * @returns {boolean} true表示两个数组相等，false表示不相等
         */
        equals: (arr1, arr2) => {
            if (Array.isArray(arr1) && Array.isArray(arr2)) {
                // 首先判断数组的长度是否相等
                if (arr1.length !== arr2.length) {
                    return false;
                }

                // 然后判断数组中的元素是否相等
                for (let i = 0; i < arr1.length; i++) {
                    if (arr1[i] !== arr2[i]) {
                        return false;
                    }
                }

                return true;
            }
        },
    },

    string: {
        search: (string, keyword) => {
            if (utils.string.vIsEmpty(keyword)) {
                return true;
            }

            if (utils.string.vIsEmpty(string)) {
                return false;
            }

            const lowerCaseString = string.toLowerCase();
            return keyword
                .split(' ')
                .every(str => lowerCaseString.includes(str.trim().toLowerCase()));
        },
        o2oOfMap: (obj, map) => {
            return obj in map ? map[obj] : null;
        },

        vIsEmpty: (str) => {
            if (typeof str !== 'string') {
                return str === null || str === undefined;
            }
            str = str.trim();
            return ["", "null", "undefined"].includes(str);
        },

        vNotEmpty: (str) => {
            return !utils.string.vIsEmpty(str);
        },
        vIfEmptyToStr: (str, toStr) => {
            return utils.string.vIsEmpty(str) ? toStr : str;
        },

        /**
         * 将个位数补零
         * @param value
         * @returns {string|*}
         */
        padZero: (value) => {
            return value < 10 ? '0' + value : value; // 将个位数补零
        }
    },

    file: {
        /**
         * 根据传入的文件名称，判断这个文件类型是否属于列表中的任意一种
         * 如：
         *  checkType("aaa.jpg",["xlsx","jpeg","doc"]) = false
         *  checkType("aaa.doc",["xlsx","jpeg","doc"]) = true
         * @param filename 文件名称，如："aaa.jpg"
         * @param types 文件类型列表，如：["xlsx","jpeg","doc","txt","jpg","png","pdf"]
         */
        checkType: (filename, types) => {
            // 提取文件的扩展名并转换为小写
            const extension = filename?.split('.').pop().toLowerCase();
            // 判断扩展名是否在提供的类型数组中
            return types.includes(extension);
        },

        /**
         * 根据传入的文件 URL，判断这个文件类型是否属于列表中的任意一种
         * 如：
         *  checkTypeByUrl("https://www.aaa.xxx.com/bbb/xxx.jpg", ["xlsx", "jpeg", "doc"]) = false
         *  checkTypeByUrl("https://www.aaa.xxx.com/bbb/xxx.doc", ["xlsx", "jpeg", "doc"]) = true
         * @param url 文件 URL，如："https://www.aaa.xxx.com/bbb/xxx.pdf"
         * @param types 文件类型列表，如：["xlsx", "jpeg", "doc", "txt", "jpg", "png", "pdf"]
         * @returns {boolean} 返回文件类型是否匹配
         */
        checkTypeByUrl: (url, types) => {
            // 提取 URL 中的文件名部分
            const filename = url?.split('/').pop();
            // 使用已有的 checkType 方法判断文件类型
            return utils.file.checkType(filename, types);
        },


        getName: (url) => {
            const urlObject = new URL(url);
            return urlObject.pathname.split('/').pop();
        },

        getPDFObjectUrl: (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const blob = new Blob([event.target.result], {type: 'application/pdf'});
                    const url = URL.createObjectURL(blob);
                    resolve(url);
                };
                reader.onerror = (event) => {
                    reject(new Error("PDF could not be read"));
                };
                reader.readAsArrayBuffer(file);
            });
        },

        getCaseInsensitiveSet: (fileTypes) => {
            const fileTypesArray = fileTypes.split(","); // 将字符串拆分为数组
            const resultSet = new Set(); // 创建一个空集合

            // 遍历数组中的每个文件类型
            fileTypesArray.forEach((fileType) => {
                const trimmedFileType = fileType.trim(); // 去掉首尾空格
                resultSet.add(trimmedFileType); // 将原始类型加入集合
                resultSet.add(trimmedFileType.toUpperCase()); // 加入大写形式
            });

            return Array.from(resultSet).join(", "); // 转换为字符串输出
        },

        getBase64: async (file) => {
            return await new Promise(function (resolve, reject) {
                let reader = new FileReader();
                let imgResult = "";
                reader.readAsDataURL(file);
                reader.onload = function () {
                    imgResult = reader.result;
                };
                reader.onerror = function (error) {
                    reject(error);
                };
                reader.onloadend = function () {
                    resolve(imgResult);
                };
            });
        },

        /**
         * 将文件url转为文件对象
         * @param url
         * @param filename
         * @returns {Promise<File>}
         */
        urlToFile: async (url, filename) => {
            const response = await fetch(url);
            const data = await response.blob();
            return new File([data], filename, {type: data.type});
        },
        async handleDownload(urls) {
            for (let i = 0; i < urls.length; i++) {
                const url = urls[i];
                let fileName = utils.date.getNow('yyyy-MM-dd-HH-mm-ss-' + utils.random.generateRandomNumber(1000, 9999) + '-' + i);

                try {
                    console.log("开始下载：url = " + url);
                    // 严格按顺序执行下载
                    await this.downloadFileWithDelay(url, fileName, 200); // 每次延迟200ms，确保浏览器处理完毕
                } catch (error) {
                    console.error("下载失败：", error);
                }
            }
        },

        downloadFileWithDelay(url, fileName, delay) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    this.downloadFile(url, fileName); // 确保文件下载
                    resolve(); // 继续下一次下载
                }, delay);
            });
        },

        downloadFile(url, fileName = 'download') {
            fetch(url, {
                method: 'GET',
                mode: 'cors', // 设置为 CORS 模式
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('网络响应失败');
                    }
                    return response.blob();
                })
                .then(blob => {
                    // 创建一个 URL 对象
                    const link = document.createElement('a');
                    const objectUrl = window.URL.createObjectURL(blob);
                    link.href = objectUrl;
                    link.download = fileName; // 设置文件名

                    // 触发下载
                    document.body.appendChild(link);
                    link.click();

                    // 清理
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl);
                })
                .catch(error => {
                    console.error('下载出错:', error);
                });
        }


    },

    url: {
        /**
         * 修改当前浏览器地址参数（不跳转页面，不刷新，不重新加载）
         * @param arg 参数名
         * @param arg_val 新的参数值
         * @returns {*|string|string}
         */
        changeURLArg: (arg, arg_val) => {
            // 获取当前浏览器完整地址
            let url = window.location.href;

            // 处理后新的地址
            var newUrl;

            // 进行参数替换
            var pattern = arg + '=([^&]*)';
            var replaceText = arg + '=' + arg_val;
            if (url.match(pattern)) {
                var tmp = '/(' + arg + '=)([^&]*)/gi';
                newUrl = url.replace(eval(tmp), replaceText);
                // return tmp;
            } else {
                if (url.match('[\?]')) {
                    newUrl = url + '&' + replaceText;
                } else {
                    newUrl = url + '?' + replaceText;
                }
            }

            utils.url.clearUrlParameters();

            // 替换地址栏
            history.replaceState(null, null, newUrl);
        },

        clearUrlParameters: () => {
            let url = window.location.href;
            if (url.indexOf('?') !== -1) {
                url = url.replace(/([?#])[^'"]*/, '');
                window.history.pushState({}, 0, url);
            }
        },

        /**
         * 获取url中的参数（适用于含有或不含有散列的URL）
         * @param name 参数名
         * @returns {string|null}
         * @constructor
         */
        getUrlParameter: (name) => {
            // 获取 URL 的 search 和 hash 部分
            var search = window.location.search.substr(1);
            var hash = window.location.hash;
            var queryStartIndex = hash.indexOf('?');
            var queryStr = queryStartIndex === -1 ? '' : hash.substr(queryStartIndex + 1);

            // 合并 search 和 hash 中的查询字符串
            var fullQueryStr = [search, queryStr].filter(Boolean).join('&');

            // 正则表达式匹配参数
            var reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)");
            var r = fullQueryStr.match(reg);
            if (r != null) return decodeURI(r[2]);
            return null;
        }

    },

    cookie: {
        clearAllCookies: () => {
            var cookies = document.cookie.split(";");
            for (var i = 0; i < cookies.length; i++) {
                var cookie = cookies[i];
                var eqPos = cookie.indexOf("=");
                var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
            }
        },

        getCookie: (name) => {
            let nameEQ = name + "=";
            let ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
            }
            return null;
        },

        setCookie: (name, value, days) => {
            let expires = "";
            if (days) {
                let date = new Date();
                date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                expires = "; expires=" + date.toUTCString();
            }
            document.cookie = name + "=" + (value || "") + expires + "; path=/";
        },

        checkCookie: (name) => {
            return utils.string.vNotEmpty(utils.cookie.getCookie(name));
        },

        deleteCookie: (name) => {
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
        }
    },

    sys: {
        /**
         * 暂停程序指定的秒数
         * @param {number} seconds - 需要暂停的秒数
         * @returns {Promise<void>} 返回一个Promise
         */
        sleep: (seconds) => {
            return new Promise(resolve => setTimeout(resolve, seconds * 1000));
        }
    },

    random: {
        /**
         * 生成指定长度的随机字符串
         * @param {Number} length - 要生成的字符串长度
         * @return {String} 随机生成的字符串
         */
        generateRandomStr: (length) => {
            let result = '';
            const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'; // 字符集，包括大小写字母和数字
            const characterLength = characters.length; // 字符集的长度

            for (let i = 0; i < length; i++) {
                const randomIndex = Math.floor(Math.random() * characterLength); // 生成随机索引
                const randomChar = characters.charAt(randomIndex); // 获取对应索引的字符
                result += randomChar; // 将字符添加到结果字符串中
            }

            return result; // 返回生成的随机字符串
        },
        /**
         * 生成指定长度的随机数字字符串
         * @param {Number} length - 要生成的数字字符串长度
         * @return {String} 随机生成的数字字符串
         */
        generateRandomNumberString: (length) => {
            let result = '';
            const characters = '0123456789'; // 数字字符集
            const characterLength = characters.length; // 数字字符集的长度

            for (let i = 0; i < length; i++) {
                const randomIndex = Math.floor(Math.random() * characterLength); // 生成随机索引
                const randomChar = characters.charAt(randomIndex); // 获取对应索引的字符
                result += randomChar; // 将字符添加到结果字符串中
            }

            return result; // 返回生成的随机数字字符串
        },
        /**
         * 生成指定范围内的随机整数
         * @param {Number} min - 随机整数的最小值
         * @param {Number} max - 随机整数的最大值
         * @return {Number} 随机生成的整数
         */
        generateRandomNumber: (min, max) => {
            return Math.floor(Math.random() * (max - min + 1)) + min; // 生成min到max之间的随机整数
        },

        /**
         * 生成指定数量的随机汉字
         * @param {Number} count - 需要生成的汉字数量
         * @return {String} 随机生成的汉字字符串
         */
        generateRandomChinese: (count) => {
            let result = '';
            for (let i = 0; i < count; i++) {
                const unicode = Math.floor(Math.random() * (0x9fa5 - 0x4e00 + 1) + 0x4e00); // 生成随机的汉字Unicode编码
                result += String.fromCharCode(unicode); // 将Unicode编码转换为汉字并添加到结果字符串中
            }
            return result; // 返回生成的随机汉字字符串
        },
        /**
         * 生成基于时间戳的随机ID
         * @return {String} 随机生成的ID
         */
        generateRandomId: () => {
            return 'id_' + Date.now().toString(36) + Math.random().toString(36).substr(2); // 返回生成的ID
        }

    },
};
